import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  useMediaQuery,
  Skeleton,
  useTheme,
  styled,
  Chip,
  alpha,
  IconButton,
} from "@mui/material";
import {
  Article as ArticleIcon,
  AccountCircle as AccountCircleIcon,
  HelpOutline as HelpOutlineIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  MenuBook as MenuBookIcon,
  FolderOff as FolderOffIcon,
} from "@mui/icons-material";
import Divider from "@mui/material/Divider";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import TopToolbar from "./TopToolbar";
import Footer from "./Footer";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f8fafc",
  fontFamily: "'Inter', 'Roboto', sans-serif",
}));

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(4),
  marginLeft: $collapsed ? "80px" : "280px",
  minWidth: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("lg")]: {
    marginLeft: $collapsed ? "80px" : "240px",
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const MobileToggleButton = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  left: 16,
  top: 16,
  zIndex: theme.zIndex.drawer + 2,
  color: theme.palette.primary.main,
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  backdropFilter: "blur(8px)",
  border: "1px solid rgba(0, 0, 0, 0.05)",
  "&:hover": {
    backgroundColor: theme.palette.common.white,
  },
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  transition: theme.transitions.create("left", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
}));

const CardPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  padding: theme.spacing(3),
  height: "100%",
  border: "1px solid #e2e8f0",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "16px",
  },
}));
const InteractiveListItem = ({ icon, title, subtitle, onClick, tag }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      p: { xs: 1.5, sm: 2 },
      mb: 1.5,
      borderRadius: "12px",
      border: "1px solid transparent",
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "#f8fafc",
        borderColor: "#e2e8f0",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
      },
      "&:hover .action-arrow": {
        transform: "translateX(4px)",
        color: "#2563eb",
      },
    }}
  >
    <Box
      sx={{
        width: { xs: 36, sm: 44 },
        height: { xs: 36, sm: 44 },
        borderRadius: "10px",
        backgroundColor: "#eff6ff",
        color: "#3b82f6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: { xs: 1.5, sm: 2 },
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: "#0f172a",
          fontSize: { xs: "0.9rem", sm: "1rem" },
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: 1.35,
          mb: 0.5,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
      >
        {tag && (
          <Chip
            label={tag}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: "#f1f5f9",
              color: "#475569",
            }}
          />
        )}
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <ArrowForwardIosIcon
      className="action-arrow"
      sx={{
        fontSize: 14,
        color: "#cbd5e1",
        ml: { xs: 1, sm: 2 },
        mt: 0.5,
        flexShrink: 0,
        transition: "all 0.2s ease",
      }}
    />
  </Box>
);
const ArticlesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [contentRef, contentInView] = useInView({
    threshold: 0.05,
    triggerOnce: true,
  });
  const [sidebarExpanded, setSidebarExpanded] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSpecificCategory, setShowSpecificCategory] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const categoryId = decodeURIComponent(searchParams.get("id") || "");

  useEffect(() => {
    if (isMobile && categoryId) {
      setShowSpecificCategory(true);
      setMobileOpen(false);
    } else if (isMobile) {
      setShowSpecificCategory(false);
    }
  }, [location.state, isMobile, categoryId]);

  const { articles = [], loading } = useSelector((state) => ({
    articles: state.support.filteredArticles || [],
    loading: state.support.loading,
  }));

  const ReduxQuestions = useSelector(
    (state) => state.support.filteredFaqs || [],
  );
  const ReduxCategories = useSelector(
    (state) => state.support.categories || [],
  );

  const parsedId = parseInt(categoryId, 10);
  const uniqueCategory =
    ReduxCategories.find((item) => item.id === parsedId) || {};

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarExpanded(!sidebarExpanded);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile && mobileOpen) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, mobileOpen]);

  // Filters
  const newReduxQuestions = uniqueCategory.categoryName
    ? ReduxQuestions.filter(
        (faq) => faq.categoryName === uniqueCategory.categoryName,
      )
    : ReduxQuestions;

  const filteredArticles = uniqueCategory.categoryName
    ? articles.filter(
        (article) => article.categoryName === uniqueCategory.categoryName,
      )
    : articles;

  const accountArticles = articles.filter(
    (article) => article.categoryName === "ACCOUNT SETTINGS",
  );

  const handleArticleClick = (article) => {
    navigate(`/articles/${article.id}`, { state: { article } });
  };

  if (isMobile && !mobileOpen && !showSpecificCategory) {
    return (
      <MobileToggleButton onClick={toggleSidebar}>
        <MenuIcon />
      </MobileToggleButton>
    );
  }
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <MainContainer>
      {isMobile && (
        <MobileToggleButton
          onClick={toggleSidebar}
          sx={{ left: mobileOpen ? 240 : 16 }}
        >
          {mobileOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </MobileToggleButton>
      )}

      <TopToolbar
        expanded={sidebarExpanded}
        toggleExpand={toggleSidebar}
        mobileOpen={mobileOpen}
      />

      <MainContent $collapsed={!sidebarExpanded && !isMobile}>
        <Box
          sx={{
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 3, md: 5 },
            mb: 4,
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)",
            color: "white",
            boxShadow: "0 10px 30px -10px rgba(30, 58, 138, 0.5)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -100,
              right: -50,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
              zIndex: 0,
            }}
          />

          <Box sx={{ position: "relative", zIndex: 2 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "white",
                mb: 1.5,
                letterSpacing: "-0.02em",
                fontSize: { xs: "1.5rem", sm: "1.9rem", md: "2.5rem" },
                wordBreak: "break-word",
                textAlign: "center",
              }}
            >
              {location.state?.categoryTitle ||
                uniqueCategory.categoryName ||
                "Support Articles"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#94a3b8",
                maxWidth: "600px",
                fontSize: { xs: "0.9rem", md: "1.1rem" },
                lineHeight: 1.6,
                textAlign: "center",
                mx: "auto",
              }}
            >
              {location.state?.categoryDescriptions ||
                uniqueCategory.descriptions ||
                "Browse our collection of technical articles, guides, and frequently asked questions."}
            </Typography>
          </Box>
        </Box>
        <Box ref={contentRef} sx={{ pb: 4 }}>
          <Grid
            container
            spacing={{ xs: 2.5, md: 4 }}
            sx={{ alignItems: "stretch" }}
          >
            <Grid item xs={12} lg={7}>
              <AnimatePresence>
                {contentInView && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ height: "100%" }}
                  >
                    <CardPanel>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: "10px",
                            backgroundColor: "#f1f5f9",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <ArticleIcon sx={{ color: "#3b82f6" }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: { xs: "1.05rem", sm: "1.25rem" },
                          }}
                        >
                          {location.state?.categoryTitle ||
                          uniqueCategory.categoryName
                            ? `${location.state?.categoryTitle || uniqueCategory.categoryName} Guides`
                            : "Technical Guides"}
                        </Typography>
                      </Box>

                      {loading ? (
                        <Box
                          sx={{
                            py: 6,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <CircularProgress
                            size={40}
                            sx={{ color: "#3b82f6", mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Loading articles...
                          </Typography>
                        </Box>
                      ) : filteredArticles.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: "center" }}>
                          <FolderOffIcon
                            sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }}
                          />
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: "#475569" }}
                          >
                            No articles available yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                            Check back later for updates to this module.
                          </Typography>
                        </Box>
                      ) : (
                        <motion.div
                          variants={containerVariants}
                          initial="hidden"
                          animate="show"
                        >
                          {filteredArticles.map((article) => (
                            <motion.div
                              key={article.id}
                              variants={itemVariants}
                            >
                              <InteractiveListItem
                                icon={<MenuBookIcon sx={{ fontSize: 22 }} />}
                                title={article.title}
                                tag={article.categoryName || article.moduleName}
                                onClick={() => handleArticleClick(article)}
                              />
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </CardPanel>
                  </motion.div>
                )}
              </AnimatePresence>
            </Grid>
            <Grid item xs={12} lg={5}>
              <AnimatePresence>
                {contentInView && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ height: "100%" }}
                  >
                    <CardPanel
                      sx={{ display: "flex", flexDirection: "column" }}
                    >
                      {/* Account Settings */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: "10px",
                            backgroundColor: "#eef2ff",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <AccountCircleIcon sx={{ color: "#6366f1" }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: { xs: "1.05rem", sm: "1.25rem" },
                          }}
                        >
                          Account Settings
                        </Typography>
                      </Box>

                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                      >
                        {accountArticles.length > 0 ? (
                          accountArticles.map((item) => (
                            <motion.div key={item.id} variants={itemVariants}>
                              <InteractiveListItem
                                icon={<ArticleIcon sx={{ fontSize: 20 }} />}
                                title={item.title}
                                onClick={() => handleArticleClick(item)}
                              />
                            </motion.div>
                          ))
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: "#94a3b8", pl: 1 }}
                          >
                            No account guides available.
                          </Typography>
                        )}
                      </motion.div>
                      <Divider
                        sx={{ my: { xs: 3, sm: 4 }, borderColor: "#e2e8f0" }}
                      />
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: "10px",
                            backgroundColor: "#f0fdf4",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <HelpOutlineIcon sx={{ color: "#10b981" }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: { xs: "1.05rem", sm: "1.25rem" },
                          }}
                        >
                          Frequently Asked
                        </Typography>
                      </Box>

                      {loading ? (
                        <Box>
                          {[...Array(3)].map((_, i) => (
                            <Skeleton
                              key={i}
                              variant="rounded"
                              height={70}
                              sx={{ mb: 2, borderRadius: "12px" }}
                            />
                          ))}
                        </Box>
                      ) : newReduxQuestions.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            No FAQs available for this module.
                          </Typography>
                        </Box>
                      ) : (
                        <motion.div
                          variants={containerVariants}
                          initial="hidden"
                          animate="show"
                        >
                          {newReduxQuestions.map((faq) => (
                            <motion.div
                              key={faq._id || faq.id}
                              variants={itemVariants}
                            >
                              <InteractiveListItem
                                icon={<HelpOutlineIcon sx={{ fontSize: 22 }} />}
                                title={faq.question || faq.title}
                                tag="FAQ"
                                onClick={() =>
                                  navigate(`/faqs/${faq._id || faq.id}`, {
                                    state: { faq },
                                  })
                                }
                              />
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </CardPanel>
                  </motion.div>
                )}
              </AnimatePresence>
            </Grid>
          </Grid>
        </Box>
      </MainContent>

      <Footer collapsed={!sidebarExpanded && !isMobile} />
    </MainContainer>
  );
};

export default ArticlesPage;
